import { GlobalLimits, IHasOrgId, IHasSchedule, IHasSettings, IWarehouse } from './';
import { INovaEntity, NovaId } from './base';
import type { Maybe } from './globals';
import { iEqual } from './js-helpers/str';
import { IOrg } from './org';

export enum LoadTypeDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  InOut = 'Inbound/Outbound',
  Load = 'Load',
  Unload = 'Unload'
}

export enum LoadTypeOperation {
  Live = 'Live',
  Drop = 'Drop',
  Other = 'Other'
}

export enum LoadTypeEquipmentType {
  DryVan = 'Dry Van',
  Flatbed = 'Flatbed',
  Reefer = 'Reefer',
  Trailer = 'Trailer',
  Container = 'Container',
  Other = 'Other'
}

export enum LoadTypeTransportationMode {
  FTL = 'FTL',
  PTL = 'PTL',
  Other = 'Other'
}

export enum LoadTypeType {
  Standard = 'Standard',
  Preparation = 'Preparation'
}

export const MAX_LOAD_TYPES_PER_WAREHOUSE = GlobalLimits.MAX_LOAD_TYPES_PER_WAREHOUSE.value;

export interface ILoadType extends INovaEntity, IHasOrgId, IHasSettings, IHasSchedule {
  name: string;
  duration_min: number;
  orgId: string;
  org?: Maybe<IOrg>;
  warehouseId?: Maybe<NovaId>;
  warehouse?: Maybe<IWarehouse>;
  direction: LoadTypeDirection;
  operation?: Maybe<LoadTypeOperation>;
  equipmentType?: Maybe<LoadTypeEquipmentType>;
  transportationMode?: Maybe<LoadTypeTransportationMode>;
  type: LoadTypeType;
  allowCarrierScheduling: boolean;
  description?: Maybe<string>;
}

export function isStandardLoadType(loadtype: ILoadType): boolean {
  return loadtype.type === LoadTypeType.Standard;
}

export function isPreparationLoadType(loadtype: ILoadType): boolean {
  return loadtype.type === LoadTypeType.Preparation;
}

// LoadTypeDirection/LoadTypeEquipmentType values that only apply to Preparation load types,
// gated behind the enable-preparation-appointments feature flag.
export const PREPARATION_ONLY_LOAD_TYPE_DIRECTIONS: LoadTypeDirection[] = [
  LoadTypeDirection.Load,
  LoadTypeDirection.Unload
];

export const PREPARATION_ONLY_LOAD_TYPE_EQUIPMENT_TYPES: LoadTypeEquipmentType[] = [
  LoadTypeEquipmentType.Trailer,
  LoadTypeEquipmentType.Container
];

export function isPreparationOnlyLoadTypeDirection(direction: LoadTypeDirection): boolean {
  return PREPARATION_ONLY_LOAD_TYPE_DIRECTIONS.includes(direction);
}

export function isPreparationOnlyLoadTypeEquipmentType(
  equipmentType: LoadTypeEquipmentType
): boolean {
  return PREPARATION_ONLY_LOAD_TYPE_EQUIPMENT_TYPES.includes(equipmentType);
}

export function isWarehouseLoadType(loadtype: ILoadType): boolean {
  return Boolean(loadtype.warehouseId);
}

export function getLoadtypeShortDirection(loadtype: ILoadType): string {
  if (!loadtype?.direction) {
    return '';
  }

  if (iEqual(loadtype.direction, LoadTypeDirection.Inbound)) {
    return 'IN';
  }

  if (iEqual(loadtype.direction, LoadTypeDirection.Outbound)) {
    return 'OUT';
  }

  if (iEqual(loadtype.direction, LoadTypeDirection.InOut)) {
    return 'IN/OUT';
  }

  return loadtype.direction;
}
